import type {
  CreateUserEmployeePayload,
  CursorPaginatedResponse,
  Employee,
  EmployeeListParams,
  EmployeeUser,
  InviteUserPayload,
} from '@/libs/interfaces'

import { MOCK_EMPLOYEE_ID, mockEmployees, mockEmployeeUser } from '../fixtures/seed'
import { delay, genID, matchesSearch, paginate } from '../helpers'
import { createEntityStore } from '../store'

const employeeStore = createEntityStore<Employee, 'employeeID'>(mockEmployees, 'employeeID')

const filterEmployees = (params: EmployeeListParams): Employee[] =>
  employeeStore
    .getAll()
    .filter((employee) =>
      matchesSearch(employee, params.search, ['firstName', 'lastName', 'email', 'shortUserName']),
    )

export const employeesApiMock = {
  create: async (body: Partial<Employee>): Promise<Partial<Employee>> => {
    const employee: Employee = {
      employeeID: genID('employee'),
      firstName: '',
      lastName: '',
      shortUserName: '',
      userActive: true,
      signature: '',
      employeeHourlyRate: 0,
      employeeHourlyRateCurrency: 'SEK',
      employeePin: '',
      employeeComment: '',
      ...body,
    }
    employeeStore.insert(employee)
    return delay(employee)
  },
  getByID: async (params: { orgID?: string; employeeID?: string }): Promise<Partial<Employee>> => {
    const employee =
      employeeStore.getById(params.employeeID ?? '') ?? employeeStore.getById(MOCK_EMPLOYEE_ID)
    return delay(employee ?? {})
  },
  getFromUserID: async (_orgID: string, userID: string): Promise<EmployeeUser> => {
    const employee = employeeStore.find((item) => item.userID === userID)[0]
    if (!employee) return delay(mockEmployeeUser)
    return delay({
      userID: employee.userID ?? userID,
      employeeID: employee.employeeID,
      shortUserName: employee.shortUserName,
      employmentNumber: employee.employmentNumber ?? '',
      employeePersonalNumber: employee.employeePersonalNumber ?? '',
      signature: employee.signature,
      employeeComment: employee.employeeComment,
      employeeHourlyRateCurrency: employee.employeeHourlyRateCurrency,
      employeeHourlyRate: employee.employeeHourlyRate,
    })
  },
  list: async (params: EmployeeListParams): Promise<Employee[]> => delay(filterEmployees(params)),
  listCursor: async (params: EmployeeListParams): Promise<CursorPaginatedResponse<Employee>> =>
    delay(paginate(filterEmployees(params), params)),
  delete: async (params: { orgID?: string; employeeID?: string }): Promise<void> => {
    if (params.employeeID) employeeStore.remove(params.employeeID)
    return delay(undefined)
  },
  inviteUser: async (_payload: InviteUserPayload): Promise<void> => delay(undefined),
  createUserEmployee: async (_orgID: string, _payload: CreateUserEmployeePayload): Promise<void> =>
    delay(undefined),
  update: async (payload: Partial<Employee>) => {
    const updated = payload.employeeID
      ? employeeStore.update(payload.employeeID, payload)
      : undefined
    return delay(updated ?? payload)
  },
}
